import React, { Component } from 'react';
import { Provider } from 'mobx-react';
import Page from 'app/components/page/Page';
import { Box, Flex, Heading, Text } from 'core/components';
import {
  AGENT_TURN_STATUS_TYPES,
  AGENT_TURN_STOPPED_MESSAGE,
  TOOL_CALL_STATUS,
  TOOL_DECISION_REFUSAL,
  TOOL_NOT_RUN_REASON,
  TOOL_PAUSE_KIND
} from '@kentik/ui-shared/kaizen/constants';
import AgentMessage from './components/AgentMessage';
import KaizenToolResultPanel from './KaizenToolResultPanel';
import { TOOL_NAMES } from './ToolResultFactory';

/**
 * Internal debug / visual-regression harness for the AI Advisor (Kaizen) conversation
 * thread. It renders every distinct visual state of `AgentMessage` / `StepComponent`
 * side-by-side so styling can be reviewed for consistency in one place.
 *
 * Route: /v4/core/ai-advisor/debug (sudo only — see core/index.js)
 */

const BASE_TIME = '2026-06-01T12:00:00Z';

const WAIT_EXPIRES_AT = '2026-06-01T12:30:00Z';

// Minimal stub standing in for the chat session model. It holds no messages, so the
// tool result panel finds no sub-agent run; approval/explorer actions are no-ops here.
const sessionModelStub = {
  id: 'kaizen-debug-session',
  get: () => undefined,
  approveToolCall: () => Promise.resolve(),
  stopTurn: () => Promise.resolve()
};

// Fixed sign-in statuses; the connect cards' status requests are no-ops.
const mcpServersStub = {
  oauthStatusMap: new Map([
    ['debug-server-connecting', { connecting: true }],
    ['debug-server-connected', { connected: true }]
  ]),
  fetchOAuthStatus: () => Promise.resolve(),
  connectOAuth: () => Promise.resolve()
};

let toolSeq = 0;
function makeToolCall(overrides = {}) {
  toolSeq += 1;
  return {
    id: `debug-tool-${toolSeq}`,
    tool_tracking_id: `debug-track-${toolSeq}`,
    tool_name: TOOL_NAMES.WEB_SEARCH,
    agent_name: 'kaizen',
    title: 'Tool call',
    status: TOOL_CALL_STATUS.SUCCESS,
    ...overrides
  };
}

let stepSeq = 0;
function makeStep(overrides = {}) {
  stepSeq += 1;
  return {
    id: `debug-step-${stepSeq}`,
    step_number: stepSeq,
    processing_status: 'COMPLETED',
    llm_response: {},
    toolCalls: [],
    ...overrides
  };
}

let turnSeq = 0;
function makeTurn(steps, overrides = {}) {
  turnSeq += 1;
  return {
    id: `debug-turn-${turnSeq}`,
    created_at: BASE_TIME,
    processing_status: AGENT_TURN_STATUS_TYPES.COMPLETED,
    feedback: [],
    steps,
    ...overrides
  };
}

function makeConnectTurn(serverId, toolOverrides = {}, turnOverrides = {}) {
  return makeTurn(
    [
      makeStep({
        processing_status: 'AWAITING_APPROVAL',
        toolCalls: [
          makeToolCall({
            tool_name: 'mcp_jira',
            title: 'Ask Jira',
            status: TOOL_CALL_STATUS.AWAITING_APPROVAL,
            query_object: { prompt: 'List the open incidents assigned to me.' },
            pause_context: { kind: TOOL_PAUSE_KIND.CONNECT, server_id: serverId, server_name: 'Jira' },
            ...toolOverrides
          })
        ]
      })
    ],
    { processing_status: AGENT_TURN_STATUS_TYPES.AWAITING_APPROVAL, wait_expires_at: WAIT_EXPIRES_AT, ...turnOverrides }
  );
}

const SERVER_APPROVAL_ROW = {
  tool_display_name: 'Jira',
  allow_auto_approve: false,
  pause_context: { kind: TOOL_PAUSE_KIND.SERVER_APPROVAL, server_id: 'debug-server-new', server_name: 'Jira' }
};

function buildConnectFixtures() {
  return [
    {
      label: 'Connect card — authorize',
      description: 'The asker has not connected the server: Authorize, Skip, Cancel and the deadline.',
      turn: makeConnectTurn('debug-server-new')
    },
    {
      label: 'Connect card — connecting',
      description: 'The sign-in popup is open.',
      turn: makeConnectTurn('debug-server-connecting')
    },
    {
      label: 'Connect card — continue',
      description: 'The asker connected the server elsewhere: Continue, Skip or Cancel.',
      turn: makeConnectTurn('debug-server-connected')
    },
    {
      label: 'Connect card — refused',
      description: 'The server found no usable grant when Continue was sent.',
      turn: makeConnectTurn('debug-server-new', { decision_refused: TOOL_DECISION_REFUSAL.NOT_CONNECTED })
    },
    {
      label: 'Connect card — waiting for the asker',
      description: 'Another viewer of the session: Continue, Skip and Cancel, no Authorize.',
      turn: makeConnectTurn('debug-server-new'),
      isAsker: false,
      askerName: 'Ana Asker'
    },
    {
      label: 'Connect card — continuing',
      description: 'The server confirmed the grant and the answer resumed.',
      turn: makeConnectTurn(
        'debug-server-connected',
        { status: TOOL_CALL_STATUS.USER_APPROVED },
        { processing_status: AGENT_TURN_STATUS_TYPES.PROCESSING }
      )
    },
    {
      label: 'Connect card — skipped',
      description: 'The asker chose Skip; the answer continued without the server.',
      turn: makeConnectTurn(
        'debug-server-new',
        { status: TOOL_CALL_STATUS.USER_REJECTED },
        { processing_status: AGENT_TURN_STATUS_TYPES.COMPLETED }
      )
    },
    {
      label: 'Connect card — stopped',
      description: 'The asker stopped the answer while it waited.',
      turn: makeConnectTurn(
        'debug-server-new',
        { status: TOOL_CALL_STATUS.CANCELLED },
        { processing_status: AGENT_TURN_STATUS_TYPES.CANCELLED }
      )
    },
    {
      label: 'Connect card — expired',
      description: 'The wait passed its deadline.',
      turn: makeConnectTurn(
        'debug-server-new',
        { status: TOOL_CALL_STATUS.APPROVAL_TIMEOUT },
        { processing_status: AGENT_TURN_STATUS_TYPES.EXPIRED }
      )
    },
    {
      label: 'Connect card — server changed while waiting',
      description: 'An admin edited the server before Continue; the call never ran.',
      turn: makeConnectTurn(
        'debug-server-connected',
        {
          status: TOOL_CALL_STATUS.FAILED,
          error_message: 'Jira changed while this request was waiting, so this call did not run.',
          raw_result: { not_run: TOOL_NOT_RUN_REASON.CHANGED, server_name: 'Jira' }
        },
        { processing_status: AGENT_TURN_STATUS_TYPES.COMPLETED }
      )
    },
    {
      label: 'Connect card — server removed while waiting',
      description: 'The server was disabled or deleted before Continue; the call never ran.',
      turn: makeConnectTurn(
        'debug-server-connected',
        {
          status: TOOL_CALL_STATUS.FAILED,
          error_message: 'Jira is no longer available to you, so this call did not run.',
          raw_result: { not_run: TOOL_NOT_RUN_REASON.NO_LONGER_AVAILABLE, server_name: 'Jira' }
        },
        { processing_status: AGENT_TURN_STATUS_TYPES.COMPLETED }
      )
    },
    {
      label: 'Connect call — failed after it started',
      description: 'The resumed call reached the server and failed.',
      turn: makeConnectTurn(
        'debug-server-connected',
        { status: TOOL_CALL_STATUS.FAILED, error_message: 'Jira is temporarily unavailable. Try again later.' },
        { processing_status: AGENT_TURN_STATUS_TYPES.COMPLETED }
      )
    },
    {
      label: 'Connect card — long names',
      description: 'A long call title and server name on one card.',
      turn: makeConnectTurn('debug-server-new', {
        tool_name: 'mcp_jira_service_management_production',
        title: 'Ask Jira Service Management for every open incident assigned to the on-call rotation this week',
        pause_context: {
          kind: TOOL_PAUSE_KIND.CONNECT,
          server_id: 'debug-server-new',
          server_name: 'Jira Service Management (Production)'
        }
      })
    },
    {
      label: 'Server approval — asker',
      description: 'The server requires approval; approving signs in next when the asker has no grant.',
      turn: makeConnectTurn('debug-server-new', SERVER_APPROVAL_ROW, { wait_expires_at: null })
    },
    {
      label: 'Server approval — waiting for the asker',
      description: 'Another viewer of the session: Reject and Cancel, no Approve.',
      turn: makeConnectTurn('debug-server-new', SERVER_APPROVAL_ROW, { wait_expires_at: null }),
      isAsker: false,
      askerName: 'Ana Asker'
    },
    {
      label: 'Server approval — refused',
      description: "The asker's approval could not check the server.",
      turn: makeConnectTurn(
        'debug-server-new',
        { ...SERVER_APPROVAL_ROW, decision_refused: TOOL_DECISION_REFUSAL.UNAVAILABLE },
        { wait_expires_at: null }
      )
    },
    {
      label: 'Mixed — approval and connect card in one step',
      description: 'A tool waiting for approval and a server waiting for sign-in hold the same answer.',
      turn: makeTurn(
        [
          makeStep({
            processing_status: 'AWAITING_APPROVAL',
            toolCalls: [
              makeToolCall({
                tool_name: 'sum_tool',
                tool_display_name: 'Sum Tool',
                title: 'Add 4 and 5',
                status: TOOL_CALL_STATUS.AWAITING_APPROVAL,
                query_object: { numbers: [4, 5] }
              }),
              makeToolCall({
                tool_name: 'mcp_jira',
                title: 'Ask Jira',
                status: TOOL_CALL_STATUS.AWAITING_APPROVAL,
                query_object: { prompt: 'List the open incidents assigned to me.' },
                pause_context: { kind: TOOL_PAUSE_KIND.CONNECT, server_id: 'debug-server-new', server_name: 'Jira' }
              })
            ]
          })
        ],
        { processing_status: AGENT_TURN_STATUS_TYPES.AWAITING_APPROVAL, wait_expires_at: WAIT_EXPIRES_AT }
      )
    },
    {
      label: 'MCP call — not called again',
      description: 'A later call to a server the asker skipped earlier in the same answer.',
      turn: makeTurn([
        makeStep({
          toolCalls: [
            makeToolCall({
              tool_name: 'mcp_jira',
              title: 'Ask Jira again',
              status: TOOL_CALL_STATUS.FAILED,
              error_message: 'The user chose not to use Jira for this request, so it is unavailable.',
              raw_result: { not_run: TOOL_NOT_RUN_REASON.SKIPPED, server_name: 'Jira' }
            })
          ]
        })
      ])
    },
    {
      label: 'MCP delegation — access lost',
      description: 'The grant died inside the server’s own work.',
      turn: makeTurn([
        makeStep({
          toolCalls: [
            makeToolCall({
              tool_name: 'mcp_jira',
              title: 'Ask Jira',
              status: TOOL_CALL_STATUS.SUCCESS,
              raw_result: { grant_lost: true, server_name: 'Jira' }
            })
          ]
        })
      ])
    }
  ];
}

// Each entry: { label, description, turn }. Order groups related states together.
function buildFixtures() {
  return [
    {
      label: 'Reasoning only',
      description: 'A step with reasoning and no final answer — collapsible reasoning block.',
      turn: makeTurn([
        makeStep({
          processing_status: 'PROCESSING',
          llm_response: {
            reasoning:
              'I need to determine which devices are reporting the most traffic. ' +
              'Let me start by querying the data explorer for the top talkers over the last hour.'
          }
        })
      ])
    },
    {
      label: 'Final answer + feedback controls',
      description: 'Completed step with a final answer, copy button, and thumbs up/down feedback.',
      turn: makeTurn([
        makeStep({
          llm_response: {
            final_answer:
              '### Summary\n\nYour top traffic source over the last hour is **device-core-01** ' +
              'at 4.2 Gbps, followed by **device-edge-03** at 2.1 Gbps.\n\n' +
              '- Total monitored: 24 devices\n- Anomalies detected: none'
          }
        })
      ])
    },
    {
      label: 'Tool call — pending',
      description: 'A tool call still running (spinner, muted title).',
      turn: makeTurn([
        makeStep({
          processing_status: 'PROCESSING',
          toolCalls: [makeToolCall({ title: 'Searching the web…', status: undefined })]
        })
      ])
    },
    {
      label: 'Tool call — success (interactive)',
      description: 'A completed tool with a detail view. Click to open it in the right panel.',
      turn: makeTurn([
        makeStep({
          toolCalls: [
            makeToolCall({
              tool_name: TOOL_NAMES.WEB_SEARCH,
              title: 'Web search: BGP route leak best practices',
              status: TOOL_CALL_STATUS.SUCCESS
            })
          ]
        })
      ])
    },
    {
      label: 'Tool call — failed',
      description: 'A tool call that errored.',
      turn: makeTurn([
        makeStep({
          toolCalls: [makeToolCall({ title: 'Query device inventory', status: TOOL_CALL_STATUS.FAILED })]
        })
      ])
    },
    {
      label: 'Tool call — user rejected',
      description: 'A tool call the user declined to approve.',
      turn: makeTurn([
        makeStep({
          toolCalls: [
            makeToolCall({ title: 'Run device command: show config', status: TOOL_CALL_STATUS.USER_REJECTED })
          ]
        })
      ])
    },
    {
      label: 'Tool call — approval timeout',
      description: 'A tool call whose approval request timed out.',
      turn: makeTurn([
        makeStep({
          toolCalls: [makeToolCall({ title: 'Run device command: reload', status: TOOL_CALL_STATUS.APPROVAL_TIMEOUT })]
        })
      ])
    },
    {
      label: 'Tool call — awaiting approval',
      description: 'A tool call paused, showing the approve/reject confirmation actions.',
      turn: makeTurn([
        makeStep({
          toolCalls: [
            makeToolCall({
              tool_name: TOOL_NAMES.DEVICE_COMMAND,
              title: 'Run device command: show interfaces',
              status: TOOL_CALL_STATUS.AWAITING_APPROVAL
            })
          ]
        })
      ])
    },
    {
      label: 'Tool call — approved by user',
      description: 'A completed tool that required and received user approval.',
      turn: makeTurn([
        makeStep({
          toolCalls: [
            makeToolCall({
              tool_name: TOOL_NAMES.DEVICE_COMMAND,
              title: 'Run device command: show interfaces',
              status: TOOL_CALL_STATUS.USER_APPROVED,
              approved_by_user_name: 'Ana Asker'
            })
          ]
        })
      ])
    },
    {
      label: 'Sub-agent delegation — waiting (Math Agent)',
      description: 'A delegation tool still running; surfaces "Waiting for Math Agent to respond…".',
      turn: makeTurn([
        makeStep({
          processing_status: 'PROCESSING',
          toolCalls: [
            makeToolCall({ tool_name: TOOL_NAMES.MATH_AGENT, title: 'Delegate to Math Agent', status: undefined })
          ]
        })
      ])
    },
    {
      label: 'Sub-agent delegation — waiting (Synthetics Agent)',
      description: 'A delegation tool still running; surfaces "Waiting for Synthetics Agent to respond…".',
      turn: makeTurn([
        makeStep({
          processing_status: 'PROCESSING',
          toolCalls: [
            makeToolCall({
              tool_name: TOOL_NAMES.SYNTHETICS_MONITORING_AGENT,
              title: 'Delegate to Synthetics Agent',
              status: undefined
            })
          ]
        })
      ])
    },
    {
      label: 'Sub-agent group — processing',
      description: 'Inline summary card for a sub-agent whose tool calls are still running. Click to open transcript.',
      turn: makeTurn([
        makeStep({
          processing_status: 'PROCESSING',
          llm_response: { reasoning: 'Delegating the synthetics question to the Synthetics Agent.' },
          toolCalls: [
            makeToolCall({
              tool_name: TOOL_NAMES.SYNTHETICS,
              agent_name: 'synthetics_agent',
              title: 'Get synthetic test status',
              status: TOOL_CALL_STATUS.SUCCESS
            }),
            makeToolCall({
              tool_name: TOOL_NAMES.SYNTHETICS_TEST_RESULTS,
              agent_name: 'synthetics_agent',
              title: 'Get test results',
              status: undefined
            })
          ]
        })
      ])
    },
    {
      label: 'Sub-agent group — completed',
      description: 'Inline summary card for a sub-agent whose tool calls all completed. Click to open transcript.',
      turn: makeTurn([
        makeStep({
          llm_response: { reasoning: 'Delegating the synthetics question to the Synthetics Agent.' },
          toolCalls: [
            makeToolCall({
              tool_name: TOOL_NAMES.SYNTHETICS,
              agent_name: 'synthetics_agent',
              title: 'Get synthetic test status',
              status: TOOL_CALL_STATUS.SUCCESS
            }),
            makeToolCall({
              tool_name: TOOL_NAMES.SYNTHETICS_TEST_RESULTS,
              agent_name: 'synthetics_agent',
              title: 'Get test results',
              status: TOOL_CALL_STATUS.SUCCESS
            })
          ]
        })
      ])
    },
    {
      label: 'Sub-agent run — processing',
      description:
        'A delegation whose run is still going. The run renders in the sub-agent panel, not the main thread.',
      turn: (() => {
        const delegation = makeToolCall({
          tool_name: 'synthetics_agent',
          title: 'Ask the Synthetics Agent',
          status: undefined
        });
        const delegatingStep = makeStep({
          llm_response: { reasoning: 'Delegating the synthetics question to the Synthetics Agent.' },
          toolCalls: [delegation]
        });
        // Saved-row shape: a run's steps name their delegation and the step that made it.
        const runStep = (overrides) =>
          makeStep({ parent_step_id: delegatingStep.id, parent_tool_call_id: delegation.id, ...overrides });
        return makeTurn([
          delegatingStep,
          runStep({
            agent_name: 'synthetics_agent',
            processing_status: 'PROCESSING',
            llm_response: { reasoning: 'Checking synthetic test health.' },
            toolCalls: [
              makeToolCall({
                tool_name: TOOL_NAMES.SYNTHETICS,
                agent_name: 'synthetics_agent',
                title: 'Get synthetic test status',
                status: TOOL_CALL_STATUS.SUCCESS
              })
            ]
          }),
          runStep({
            agent_name: 'synthetics_agent',
            processing_status: 'PROCESSING',
            toolCalls: [
              makeToolCall({
                tool_name: TOOL_NAMES.SYNTHETICS_TRACE_RESULTS,
                agent_name: 'synthetics_agent',
                title: 'Get trace results',
                status: undefined
              })
            ]
          })
        ]);
      })()
    },
    {
      label: 'Sub-agent run — completed',
      description: 'A finished delegation. Its run renders in the sub-agent panel, not the main thread.',
      turn: (() => {
        const delegation = makeToolCall({
          tool_name: 'synthetics_agent',
          title: 'Ask the Synthetics Agent',
          status: TOOL_CALL_STATUS.SUCCESS
        });
        const delegatingStep = makeStep({
          llm_response: { reasoning: 'Delegating the synthetics question to the Synthetics Agent.' },
          toolCalls: [delegation]
        });
        // Saved-row shape: a run's steps name their delegation and the step that made it.
        const runStep = (overrides) =>
          makeStep({ parent_step_id: delegatingStep.id, parent_tool_call_id: delegation.id, ...overrides });
        return makeTurn([
          delegatingStep,
          runStep({
            agent_name: 'synthetics_agent',
            llm_response: { reasoning: 'Checking synthetic test health.' },
            toolCalls: [
              makeToolCall({
                tool_name: TOOL_NAMES.SYNTHETICS,
                agent_name: 'synthetics_agent',
                title: 'Get synthetic test status',
                status: TOOL_CALL_STATUS.SUCCESS
              })
            ]
          }),
          runStep({
            agent_name: 'synthetics_agent',
            toolCalls: [
              makeToolCall({
                tool_name: TOOL_NAMES.SYNTHETICS_TRACE_RESULTS,
                agent_name: 'synthetics_agent',
                title: 'Get trace results',
                status: TOOL_CALL_STATUS.SUCCESS
              })
            ]
          })
        ]);
      })()
    },
    {
      label: 'Mixed: reasoning + sub-agent + final answer',
      description: 'A realistic multi-step turn combining a main step, a sub-agent group, and a final answer.',
      turn: makeTurn([
        makeStep({
          processing_status: 'PROCESSING',
          llm_response: { reasoning: 'This needs synthetics data, so I will delegate to the Synthetics Agent.' },
          toolCalls: [
            makeToolCall({
              tool_name: TOOL_NAMES.SYNTHETICS,
              agent_name: 'synthetics_agent',
              title: 'Get synthetic test status',
              status: TOOL_CALL_STATUS.SUCCESS
            })
          ]
        }),
        makeStep({
          llm_response: {
            final_answer: 'All 12 synthetic tests are passing. The slowest is **us-east → eu-west** at 142 ms.'
          }
        })
      ])
    },
    ...buildConnectFixtures(),
    {
      label: 'Turn — stopped',
      description: 'A turn the user stopped mid-response.',
      turn: makeTurn(
        [
          makeStep({
            llm_response: { reasoning: 'Starting to gather data…' },
            processing_status: 'COMPLETED',
            error_message: AGENT_TURN_STOPPED_MESSAGE
          })
        ],
        { processing_status: 'COMPLETED', error_message: AGENT_TURN_STOPPED_MESSAGE }
      )
    },
    {
      label: 'Turn — stopped during a tool call',
      description: 'A turn the user stopped while a tool ran; the tool was cancelled.',
      turn: makeTurn(
        [
          makeStep({
            llm_response: { reasoning: 'Querying the top talkers…' },
            processing_status: 'COMPLETED',
            toolCalls: [
              makeToolCall({
                title: 'Query top talkers',
                status: TOOL_CALL_STATUS.FAILED,
                error_message: AGENT_TURN_STOPPED_MESSAGE
              })
            ]
          })
        ],
        { processing_status: 'COMPLETED', error_message: AGENT_TURN_STOPPED_MESSAGE }
      )
    },
    {
      label: 'Turn — stopped while waiting',
      description: 'A turn the user stopped while it waited.',
      turn: makeTurn(
        [makeStep({ llm_response: { reasoning: 'Starting to gather data…' }, processing_status: 'PROCESSING' })],
        { processing_status: AGENT_TURN_STATUS_TYPES.CANCELLED }
      )
    },
    {
      label: 'Turn — expired',
      description: 'A turn whose wait passed its deadline.',
      turn: makeTurn(
        [makeStep({ llm_response: { reasoning: 'Starting to gather data…' }, processing_status: 'PROCESSING' })],
        { processing_status: AGENT_TURN_STATUS_TYPES.EXPIRED }
      )
    },
    {
      label: 'Turn — error',
      description: 'A turn that failed with a turn-level error.',
      turn: makeTurn([makeStep({ llm_response: { reasoning: 'Attempting to query…' } })], {
        error_message: 'Internal error while processing the request.'
      })
    },
    {
      label: 'Step — error',
      description: 'A step-level error callout.',
      turn: makeTurn([
        makeStep({
          processing_status: 'PROCESSING',
          llm_response: { reasoning: 'Querying device inventory…' },
          error_message: 'TOOL_TIMEOUT'
        })
      ])
    }
  ];
}

class KaizenDebugView extends Component {
  state = {
    selectedToolResults: []
  };

  fixtures = buildFixtures();

  handleSelectToolResult = (tool) => {
    this.setState((prevState) => {
      const toolExists = prevState.selectedToolResults.some((existing) => existing.id === tool.id);
      if (toolExists) {
        return null;
      }
      return { selectedToolResults: [...prevState.selectedToolResults, tool] };
    });
  };

  handleCloseToolResult = (tool) => {
    this.setState((prevState) => ({
      selectedToolResults: prevState.selectedToolResults.filter((t) => t.id !== tool.id)
    }));
  };

  handleCloseToolResults = () => {
    this.setState({ selectedToolResults: [] });
  };

  handleNoop = () => {};

  render() {
    const { selectedToolResults } = this.state;

    return (
      <Page title="AI Advisor — Component States (Debug)" scrolls>
        <Flex height="100%" width="100%" overflow="hidden">
          <Box flex={1} overflow="auto" p={3}>
            <Heading level={3} mb={1}>
              AI Advisor — Conversation Component States
            </Heading>
            <Text muted as="p" mb={3}>
              Every distinct visual state of <code>AgentMessage</code> / <code>StepComponent</code>, rendered together
              for style review. Interactive cards open their details in the right panel.
            </Text>

            <Provider $mcpServers={mcpServersStub}>
              <Flex flexDirection="column" gap={4}>
                {this.fixtures.map(({ label, description, turn, isAsker = true, askerName }) => (
                  <Box key={turn.id} borderBottom="thin" pb={3}>
                    <Heading level={5} mb={0}>
                      {label}
                    </Heading>
                    <Text small muted as="p" mb={2}>
                      {description}
                    </Text>
                    <Box bg="decks.background" p={2} borderRadius={4}>
                      <AgentMessage
                        turn={turn}
                        isAsker={isAsker}
                        askerName={askerName}
                        onSelectToolResult={this.handleSelectToolResult}
                        onSendToExplorer={this.handleNoop}
                        onShowAgentDataInSidebar={this.handleNoop}
                        isCopilotMode={false}
                        isExistingChatLoading={false}
                        sessionModel={sessionModelStub}
                      />
                    </Box>
                  </Box>
                ))}
              </Flex>
            </Provider>
          </Box>

          {selectedToolResults.length > 0 && (
            <Box width={480} borderLeft="thin" overflow="hidden">
              <KaizenToolResultPanel
                tools={selectedToolResults}
                onCloseToolResult={this.handleCloseToolResult}
                onClose={this.handleCloseToolResults}
                onSelectToolResult={this.handleSelectToolResult}
                sessionModel={sessionModelStub}
              />
            </Box>
          )}
        </Flex>
      </Page>
    );
  }
}

export default KaizenDebugView;
